/**
 * File encoding and validation utilities for the playground feature.
 * Supports encoding files to base64 and validating file types and sizes.
 */

/**
 * Supported image MIME types
 */
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

/**
 * Supported audio MIME types
 */
export const SUPPORTED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'] as const;

/**
 * All supported file types
 */
export const SUPPORTED_FILE_TYPES = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_AUDIO_TYPES] as const;

/**
 * Default maximum file size (10MB)
 */
export const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Encodes a file to base64 format.
 * Removes the data URL prefix to return only the base64-encoded content.
 *
 * @param file - The file to encode
 * @returns Promise resolving to the base64-encoded string
 * @throws Error if file reading fails
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'test.png', { type: 'image/png' });
 * const base64 = await encodeFileToBase64(file);
 * console.log(base64); // "Y29udGVudA=="
 * ```
 */
export async function encodeFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${reader.error?.message || 'Unknown error'}`));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Validates a file against allowed types and maximum size.
 *
 * @param file - The file to validate
 * @param allowedTypes - Array of allowed MIME types (defaults to all supported types)
 * @param maxSize - Maximum file size in bytes (defaults to 10MB)
 * @returns Validation result with error message if invalid
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'test.png', { type: 'image/png' });
 * const result = validateFile(file, SUPPORTED_IMAGE_TYPES, 5 * 1024 * 1024);
 * if (!result.valid) {
 *   console.error(result.error);
 * }
 * ```
 */
export function validateFile(
  file: File,
  allowedTypes: readonly string[] = SUPPORTED_FILE_TYPES,
  maxSize: number = DEFAULT_MAX_FILE_SIZE
): FileValidationResult {
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
    return {
      valid: false,
      error: `File type "${file.type}" (${fileExtension}) is not supported. Allowed types: ${allowedTypes.join(', ')}`,
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    const maxSizeMB = (maxSize / 1024 / 1024).toFixed(2);
    return {
      valid: false,
      error: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
}

/**
 * Checks if a file is an image type
 */
export function isImageFile(file: File): boolean {
  return SUPPORTED_IMAGE_TYPES.includes(file.type as (typeof SUPPORTED_IMAGE_TYPES)[number]);
}

/**
 * Checks if a file is an audio type
 */
export function isAudioFile(file: File): boolean {
  return SUPPORTED_AUDIO_TYPES.includes(file.type as (typeof SUPPORTED_AUDIO_TYPES)[number]);
}

/**
 * Gets a human-readable file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
